import { Box } from '@mui/material';
import Autosuggest from 'components/controls/Autosuggest';
import Label from 'components/@home/drawers/AppDrawer/Form/Label';
import { normalizeEmployeeReportOptions } from 'components/@home/@documents/SentDocuments/edit-steps/editTemplateFlow';
import { func, object } from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HR_BOT_APP_ID } from 'services/constants';
import apps from 'store/app/entities/apps/action';
import { useLazyListQuery } from 'store/app/entities/EmployeesApiSlice';
import authCompanyId from 'store/selectors/authCompanyId';
import loadOptionsLazyQuery from 'utils/loadOptionsLazyQuery';
import removeAccents from 'utils/removeAccents';
import { I18n } from 'utils/i18n';

/**
 * Docubot / HrBot delivery audit: DM to a group or employee when a bulk send completes.
 * Same options as legacy {@link components/@home/drawers/HrBotDrawer/EditTemplate.jsx}.
 */
const DeliveryAuditReportField = ({ sendReportTo = { value: -1 }, onChange }) => {
  const companyId = useSelector(authCompanyId);
  const app = useSelector(state => state.apps.apps?.[HR_BOT_APP_ID]);
  const [triggerEmployeesQuery] = useLazyListQuery();
  const dispatch = useDispatch();
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  useEffect(() => {
    setGroupsLoaded(false);
  }, []);

  useEffect(() => {
    if (!app?._id) return;
    if (!groupsLoaded) {
      dispatch(apps.getGroups(app._id))
        .then(() => setGroupsLoaded(true))
        .catch(() => setGroupsLoaded(true));
    }
  }, [app?._id, dispatch, groupsLoaded]);

  const loadOptions = useCallback(
    (search, ...args) => {
      return loadOptionsLazyQuery(search, ...args).then(({ options, ...rest }) => {
        const searchString = removeAccents(search.toLowerCase());
        return {
          ...rest,
          options: [
            {
              label: I18n.t('HrBot.EditTemplate.Do not send'),
              value: -1,
            },
            {
              label: I18n.t('HrBot.EditTemplate.Groups'),
              options:
                app?.groups
                  ?.map(channel => ({
                    value: channel._id,
                    type: 'group',
                    label: channel.name,
                  }))
                  .filter(
                    ({ label }) =>
                      !search ||
                      removeAccents(label || '')
                        .toLowerCase()
                        .includes(searchString),
                  ) || [],
            },
            {
              label: I18n.t('HrBot.EditTemplate.People'),
              options: normalizeEmployeeReportOptions(options),
            },
          ],
        };
      });
    },
    [app?.groups],
  );

  /** Pass full option shape so AsyncPaginate can show the label after remount (OMM-684). */
  const autosuggestValue = useMemo(() => {
    const v = sendReportTo?.value;
    if (v === -1 || v === '-1' || v == null || !sendReportTo?.type) {
      return { label: I18n.t('HrBot.EditTemplate.Do not send'), value: -1 };
    }
    let label = sendReportTo.label;
    if (!label && sendReportTo.type === 'group') {
      label = app?.groups?.find(c => String(c._id) === String(v))?.name;
    }
    return {
      label: label || String(v),
      value: v,
      type: sendReportTo.type,
    };
  }, [sendReportTo, app?.groups]);

  return (
    <Box sx={{ width: '100%' }}>
      <Label>{I18n.t('HrBot.EditTemplate.Send report to')}</Label>
      <Autosuggest
        key={groupsLoaded ? 'loaded' : 'loading'}
        fullWidth
        value={autosuggestValue}
        onChange={onChange}
        loadOptions={loadOptions}
        additional={{
          companyId,
          page: 1,
          trigger: triggerEmployeesQuery,
        }}
      />
    </Box>
  );
};

DeliveryAuditReportField.propTypes = {
  sendReportTo: object,
  onChange: func.isRequired,
};

export default DeliveryAuditReportField;
